import { useEffect } from 'react';
import { postEvents } from './track';
import {
  getPathBeforeCurrent,
  classifyEntryHit,
  internalSourceFromPath,
  isReloadEntry,
} from './pageView';
import { useCurrentUserId } from '../hooks/useCurrentUserId';

// First-party buyer demand tracking: one 'search' event per demand state (term + category trail +
// filters) the user lands on, sent when the search page's results settle. Design and semantics:
// server/analytics/README.md (search_events). Reuses the page_view navigation state for source
// classification; a one-shot marker set at the topbar click sites ('navbar') always beats the
// previous-path fallback, so 'internal_search' means exactly: refined via the search page's own UI.

// One-shot source marker. TTL bounds it so an aborted navigation cannot mis-tag a later search;
// generous because an AI search can settle slowly on a cold semantic backend.
const MARKER_TTL_MS = 30_000;
let pendingSource = null;
let pendingSourceAt = 0;

export const markSearchSource = source => {
  pendingSource = source;
  pendingSourceAt = Date.now();
};

const consumePendingSource = () => {
  const source =
    pendingSource && Date.now() - pendingSourceAt <= MARKER_TTL_MS ? pendingSource : null;
  pendingSource = null;
  return source;
};

// Params that are part of the search URL but not demand signal. Categories and the typed term are
// promoted to their own columns; sort/pagination/map state is excluded from the event entirely.
const NON_FILTER_PARAM_NAMES = [
  'pub_categoryLevel1',
  'pub_categoryLevel2',
  'pub_categoryLevel3',
  'keywords',
  'sort',
  'page',
  'bounds',
  'origin',
  'address',
  'mapSearch',
];

const pickFilters = params => {
  const filters = {};
  Object.keys(params || {}).forEach(key => {
    if (NON_FILTER_PARAM_NAMES.includes(key)) return;
    const value = params[key];
    if (value == null || value === '') return;
    filters[key] = String(value);
  });
  return filters;
};

// Consecutive-duplicate suppression across the pageload (module-level, like page_views'
// previousPath): effect re-runs and settles of the same state never double-log; pagination, sort,
// and map changes are excluded from the signature so they can never produce a new row.
let lastSignature = null;

const signatureOf = ({ searchTerm, categoryL1, categoryL2, categoryL3, filters }) =>
  [
    searchTerm || '',
    categoryL1 || '',
    categoryL2 || '',
    categoryL3 || '',
    Object.keys(filters)
      .sort()
      .map(key => `${key}=${filters[key]}`)
      .join('&'),
  ].join('|');

const normalizeTerm = value => {
  if (value == null) return null;
  const term = String(value).trim();
  return term || null;
};

export const logSearchEvent = ({ searchTerm, params, resultCount, userId }) => {
  if (typeof window === 'undefined') return;

  // Consume the marker up front: if this navigation lands on a suppressed state (duplicate or
  // reload), the marker must die with it instead of mis-tagging a later unrelated refinement.
  const markedSource = consumePendingSource();

  const filters = pickFilters(params);
  const event = {
    searchTerm: normalizeTerm(searchTerm),
    categoryL1: params?.pub_categoryLevel1 || null,
    categoryL2: params?.pub_categoryLevel2 || null,
    categoryL3: params?.pub_categoryLevel3 || null,
    filters,
  };

  const signature = signatureOf(event);
  if (signature === lastSignature) return;
  lastSignature = signature;

  // A reload of the search you are already on is not renewed demand (same rule as page_views).
  if (getPathBeforeCurrent() === null && isReloadEntry()) return;

  const prevPath = getPathBeforeCurrent();
  const source =
    markedSource || (prevPath === null ? classifyEntryHit() : internalSourceFromPath(prevPath));

  postEvents([
    {
      type: 'search',
      ...event,
      resultCount: Number.isInteger(resultCount) ? resultCount : null,
      userId: userId || null,
      source,
    },
  ]);
};

/**
 * Effect-only logger rendered by the search page variants. Logs once per settled demand state.
 * Props are raw references only; all derivation happens here so no expression at the host page's
 * render seam can ever throw into the page.
 *
 * @param {boolean} active - true when the search has settled (results + count are for the URL state)
 * @param {string} searchTerm - what the user typed (q, or the legacy keywords param), if anything
 * @param {Object} storeSearchParams - the store's searchParams the results belong to (null before
 *   the first search). searchParamsAreInSync ignores q, so on a q-to-q transition there is one
 *   render frame where the URL already shows the new term but pagination still counts the old one;
 *   comparing the terms closes it.
 * @param {Object} params - validated search params (urlQueryParams from searchParamsPicker)
 * @param {number} resultCount - total items for the settled search
 */
export const SearchEventLogger = ({ active, searchTerm, storeSearchParams, params, resultCount }) => {
  const userId = useCurrentUserId();

  useEffect(() => {
    if (!active) return;
    const storeSearchTerm = storeSearchParams?.q || storeSearchParams?.keywords;
    if (normalizeTerm(searchTerm) !== normalizeTerm(storeSearchTerm)) return;
    logSearchEvent({ searchTerm, params, resultCount, userId });
  });

  return null;
};

export const __testing = {
  resetForTesting: () => {
    lastSignature = null;
    pendingSource = null;
    pendingSourceAt = 0;
  },
};
